import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, UserPlus, Trash2, ArrowUpCircle } from 'lucide-react';
import api from '@/lib/api';
import { Card, SectionLabel } from '@/components/shared';

export default function AdminAdmins() {
  const [admins, setAdmins] = useState([]);
  const [architects, setArchitects] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [promoteId, setPromoteId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get('/admin/admins').then((r) => setAdmins(r.data)).catch(() => {});
    api.get('/admin/architects').then((r) => setArchitects(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addAdmin = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || form.password.length < 6) { toast.error('E-posta ve en az 6 karakter şifre gerekli'); return; }
    setBusy(true);
    try {
      await api.post('/admin/admins', form);
      toast.success('Yönetici eklendi');
      setForm({ name: '', email: '', password: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'İşlem başarısız'); }
    finally { setBusy(false); }
  };

  const promote = async () => {
    if (!promoteId) { toast.error('Bir mimar seçin'); return; }
    try {
      await api.post(`/admin/architects/${promoteId}/make-admin`);
      toast.success('Mimar yönetici yapıldı');
      setPromoteId('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'İşlem başarısız'); }
  };

  const revoke = async (userId) => {
    if (!window.confirm('Bu kullanıcının yönetici yetkisi kaldırılsın mı?')) return;
    try {
      await api.post(`/admin/admins/${userId}/revoke`);
      toast.success('Yetki kaldırıldı');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'İşlem başarısız'); }
  };

  return (
    <div className="fade-up max-w-3xl" data-testid="admin-admins">
      <h1 className="font-serif text-3xl text-ink mb-1">Yöneticiler</h1>
      <p className="text-ink/50 text-sm mb-6">Yönetici hesaplarını ekleyin, terfi ettirin veya yetkiyi kaldırın</p>

      <SectionLabel>Mevcut Yöneticiler</SectionLabel>
      <div className="space-y-2 mb-8" data-testid="admins-list">
        {admins.map((a) => (
          <Card key={a.user_id} className="p-4 flex items-center justify-between" data-testid={`admin-row-${a.user_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-9 h-9 rounded-full border border-line flex items-center justify-center shrink-0">
                <ShieldCheck strokeWidth={1.5} className="w-4 h-4 text-gold" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink truncate">{a.name || a.email}</p>
                <p className="text-xs text-ink/50 truncate">{a.email}</p>
              </div>
            </div>
            <button onClick={() => revoke(a.user_id)} data-testid={`revoke-${a.user_id}`}
              className="inline-flex items-center gap-1 text-xs border border-redx/30 text-redx px-2.5 py-1.5 rounded-lg active:scale-95 transition-transform shrink-0">
              <Trash2 strokeWidth={1.5} className="w-3.5 h-3.5" /> Yetkiyi kaldır
            </button>
          </Card>
        ))}
      </div>

      <SectionLabel>Yeni Yönetici Ekle</SectionLabel>
      <Card className="p-5 mb-8">
        <form onSubmit={addAdmin} className="space-y-3" data-testid="add-admin-form">
          <Input placeholder="Ad Soyad" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="new-admin-name" />
          <Input placeholder="E-posta" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="new-admin-email" />
          <Input placeholder="Şifre (en az 6 karakter)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="new-admin-password" />
          <button type="submit" disabled={busy} data-testid="add-admin-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-ink text-ground rounded-xl py-3 text-sm font-semibold active:scale-95 transition-transform disabled:opacity-50">
            <UserPlus strokeWidth={1.5} className="w-4 h-4" /> {busy ? 'Ekleniyor...' : 'Yönetici Ekle'}
          </button>
        </form>
      </Card>

      <SectionLabel>Mevcut Bir Mimarı Terfi Ettir</SectionLabel>
      <Card className="p-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={promoteId} onChange={(e) => setPromoteId(e.target.value)} data-testid="promote-select"
            className="flex-1 bg-ground border border-line rounded-lg px-3 py-2.5 text-sm text-ink appearance-none focus:outline-none focus:ring-2 focus:ring-gold/40">
            <option value="">Mimar seçin...</option>
            {architects.map((a) => (
              <option key={a.id} value={a.id}>{a.name} — {a.email}</option>
            ))}
          </select>
          <button onClick={promote} data-testid="promote-submit"
            className="inline-flex items-center justify-center gap-2 border border-line text-ink rounded-xl px-4 py-2.5 text-sm font-medium active:scale-95 transition-transform">
            <ArrowUpCircle strokeWidth={1.5} className="w-4 h-4" /> Yönetici yap
          </button>
        </div>
      </Card>
    </div>
  );
}

const Input = ({ placeholder, value, onChange, testid, type = 'text' }) => (
  <input data-testid={testid} type={type} placeholder={placeholder} value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-ground border border-line rounded-lg px-3 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-colors" />
);
